import {useQuery} from "@tanstack/react-query";
import {viafouraCommentCountQueryOptions, viafouraSectionQueryOptions} from "./viafouraCommentCounts";
import {useViafouraInteractive} from "../LiveblogRenderContext";

/**
 * The live comment count for one Viafoura conversation, or `undefined` when there is no answer.
 *
 * `undefined` covers every "we do not know" case and they are deliberately not distinguished at this
 * layer: the request is still in flight, the domain is not provisioned with Viafoura, the reader
 * blocked the request, or Viafoura has never seen this container. The card renders all of them the
 * same way — as the trigger it was before counts existed — because there is no version of "we could
 * not reach Viafoura" worth putting in front of a reader.
 *
 * Both queries are disabled on Tickaroo-hosted surfaces (the editor's previews, the hosted
 * fullpage). Those resolve to no Viafoura account at all, so the lookup could only fail; disabling
 * means they make no third-party request whatsoever rather than one that is certain to be wasted.
 *
 * That takes two gates, because the surfaces announce themselves in two different ways and the
 * context alone does not catch both: the editor's post stream renders rows directly with
 * `isPreview` and leaves the context at its interactive default, so a card that knows it is inert
 * passes `enabled: false` — exactly as it already refuses to open a conversation.
 *
 * Neither query runs during server rendering, so the server render and the first client render agree
 * on "no count" and hydration cannot mismatch. The number arrives afterwards.
 */
export function useViafouraCommentCount(containerId: string, enabled = true): number | undefined {
  const interactive = useViafouraInteractive() && enabled;

  const {data: sectionUuid} = useQuery({
    ...viafouraSectionQueryOptions(),
    enabled: interactive
  });

  const {data} = useQuery({
    ...viafouraCommentCountQueryOptions(sectionUuid, containerId),
    enabled: interactive && !!sectionUuid
  });

  // `null` is the query function's "Viafoura has no answer for this container"; the component only
  // needs to know it has no number. Zero is a real count and must survive this.
  return data ?? undefined;
}
